import React, { useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { CellClickedEvent, CellStyle, ColDef, ValueFormatterParams } from "ag-grid-community";
import { useSelector } from "react-redux";
import { getListMemberTotalByCompanyUnit, IListMemberTotalByCompanyUnit } from "../../../../api/reportCustomer/reportCustomerApi";
import { selectorInfoUser } from "../../../../store/slices/infoUserSlice";
import { formatNumber } from "../../../../services/mixin";
import { IDataDefaulFilter } from "..";
import { IRank } from "../../../../api/selectOptions/selectOptionsApi";
import '../styles.scss';
import { CaretDownOutlined, CaretRightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

// ─── Types ────────────────────────────────────────────────────────────────────

interface IReportTotalMembersNode {
    id: string;
    parentId?: string;
    name: string;
    ranks: Record<string, number>;
    tong?: number;
}

interface FlatRow extends IReportTotalMembersNode {
    _level: number;
    _hasChildren: boolean;
}
interface FlatRow extends IReportTotalMembersNode {
    _level: number;
    _hasChildren: boolean;
}

// Map column field → rank title in API
const RANK_FIELD_MAP: Record<string, string> = {
    member: 'Member',
    silver: 'Silver',
    gold: 'Gold',
    plantium: 'Plantium',
};

interface Props {
    dataFilter?: IDataDefaulFilter;
    dataRank?: IRank[];
    setTotal: (e: number) => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function flattenTree(
    items: IListMemberTotalByCompanyUnit[],
    parentId?: string,
): IReportTotalMembersNode[] {

    const result: IReportTotalMembersNode[] = [];

    for (const item of items) {

        result.push({
            id: String(item.id),
            parentId,
            name: item.title,

            ranks: item.ranks ?? {},

            tong: item.total,
        });

        if (item.children?.length) {

            result.push(
                ...flattenTree(
                    item.children as IListMemberTotalByCompanyUnit[],
                    String(item.id)
                )
            );

        }
    }

    return result;
}

function buildFlatRows(
    nodes: IReportTotalMembersNode[],
    expanded: Set<string>,
): FlatRow[] {
    const childrenMap = new Map<string | undefined, IReportTotalMembersNode[]>();
    for (const n of nodes) {
        const key = n.parentId;
        if (!childrenMap.has(key)) childrenMap.set(key, []);
        childrenMap.get(key)!.push(n);
    }

    const result: FlatRow[] = [];

    function walk(parentId: string | undefined, level: number) {
        for (const node of childrenMap.get(parentId) ?? []) {
            const hasChildren = childrenMap.has(node.id);
            result.push({ ...node, _level: level, _hasChildren: hasChildren });
            if (hasChildren && expanded.has(node.id)) {
                walk(node.id, level + 1);
            }
        }
    }

    walk(undefined, 0);
    return result;
}

const fmtNum = (p: ValueFormatterParams) =>
    p.value == null ? '--' : String(formatNumber(p.value));


const countUnits = (
    data: IListMemberTotalByCompanyUnit[] | undefined | null
): number => {
    if (!data?.length) return 0;

    let count = 0;

    const countRecursive = (items: IListMemberTotalByCompanyUnit[]) => {
        items.forEach(item => {
            // Chính node này cũng tính là 1 đơn vị
            count++;

            // Đếm tiếp các node con
            if (item.children?.length) {
                countRecursive(item.children);
            }
        });
    };

    countRecursive(data);

    return count;
};


// ─── Component ────────────────────────────────────────────────────────────────

const ReportTableTotalMembers: React.FC<Props> = ({ dataFilter, dataRank, setTotal }) => {
    const { unitTemp } = useSelector(selectorInfoUser);
    const navigate = useNavigate()

    const [nodes, setNodes] = useState<IReportTotalMembersNode[]>([]);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const fetchData = async () => {
        const result = await getListMemberTotalByCompanyUnit(
            unitTemp,
            dataFilter?.unitIds,
            dataFilter?.rankIds,
            dataFilter?.fromMonth,
            dataFilter?.toMonth,
        );
        if (result?.status) {
            const flat = flattenTree(result.data ?? []);
            setNodes(flat);
            // auto-expand level 0
            const topIds = new Set(flat.filter(n => !n.parentId).map(n => n.id));
            setExpanded(topIds);

            setTotal(countUnits(result.data));

        }
    };

    useEffect(() => {
        fetchData();
    }, [dataFilter, unitTemp]);

    const toggle = (id: string) =>
        setExpanded(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const rowData = useMemo(() => buildFlatRows(nodes, expanded), [nodes, expanded]);

    const rankColumns = useMemo(() => {

        const rankSet = new Set<string>();

        nodes.forEach(node => {

            Object.keys(node.ranks ?? {}).forEach(rankName => {

                rankSet.add(rankName);

            });

        });

        return Array.from(rankSet);

    }, [nodes]);

    // const columnDefs = useMemo<ColDef<FlatRow>[]>(() => [
    //     {
    //         headerName: 'Công ty tỉnh thành',
    //         field: 'name',
    //         flex: 2,
    //         minWidth: 280,
    //         cellRenderer: (params: any) => {
    //             const row: FlatRow = params.data;
    //             const indent = row._level * 20;
    //             return (
    //                 <div
    //                     style={{ display: 'flex', alignItems: 'center', paddingLeft: indent, height: '100%', cursor: row._hasChildren ? 'pointer' : 'default' }}
    //                     onClick={() => row._hasChildren && toggle(row.id)}
    //                 >
    //                     {row._hasChildren ? (
    //                         <span style={{ marginRight: 6, fontSize: 10, color: '#0d6bc8', flexShrink: 0 }}>
    //                             {expanded.has(row.id) ? '▼' : '▶'}
    //                         </span>
    //                     ) : (
    //                         <span style={{ marginRight: 6, width: 14, flexShrink: 0 }} />
    //                     )}
    //                     <span>{row.name}</span>
    //                 </div>
    //             );
    //         },
    //     },
    //     { headerName: 'Member',   field: 'member',   flex: 1, valueFormatter: fmtNum, headerClass: 'ag-header-cell-center', cellStyle: { textAlign: 'center', cursor: 'pointer' } as CellStyle },
    //     { headerName: 'Silver',   field: 'silver',   flex: 1, valueFormatter: fmtNum, headerClass: 'ag-header-cell-center', cellStyle: { textAlign: 'center', cursor: 'pointer' } as CellStyle },
    //     { headerName: 'Gold',     field: 'gold',     flex: 1, valueFormatter: fmtNum, headerClass: 'ag-header-cell-center', cellStyle: { textAlign: 'center', cursor: 'pointer' } as CellStyle },
    //     { headerName: 'Plantium', field: 'plantium', flex: 1, valueFormatter: fmtNum, headerClass: 'ag-header-cell-center', cellStyle: { textAlign: 'center', cursor: 'pointer' } as CellStyle },
    //     { headerName: 'Tổng',    field: 'tong',     flex: 1, valueFormatter: fmtNum, headerClass: 'ag-header-cell-center', cellStyle: { textAlign: 'center' } as CellStyle },
    // ], [expanded]);
    const columnDefs = useMemo<ColDef<FlatRow>[]>(() => {

        const columns: ColDef<FlatRow>[] = [

            // =========================
            // Công ty / tỉnh thành
            // =========================
            {
                headerName: 'Công ty tỉnh thành',

                field: 'name',

                flex: 2,

                minWidth: 280,

                cellRenderer: (params: any) => {

                    const row: FlatRow = params.data;

                    const indent = row._level * 20;

                    return (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: indent,
                                height: '100%',
                                cursor: row._hasChildren
                                    ? 'pointer'
                                    : 'default',
                            }}

                            onClick={() => {

                                if (row._hasChildren) {
                                    toggle(row.id);
                                }

                            }}
                        >

                            {row._hasChildren ? (

                                <span
                                    style={{
                                        marginRight: 6,
                                        // paddingTop: 2,
                                        color: '#0d6bc8',
                                        flexShrink: 0,
                                    }}
                                >
                                    {expanded.has(row.id)
                                        ? <CaretDownOutlined style={{ fontSize: 16 }} />
                                        : <CaretRightOutlined style={{ fontSize: 16 }} />}
                                </span>

                            ) : (

                                <span
                                    style={{
                                        marginRight: 6,
                                        width: 14,
                                        flexShrink: 0,
                                    }}
                                />

                            )}

                            <span>
                                {row.name}
                            </span>

                        </div>
                    );
                },
            },

        ];

        // =========================
        // Các cột Rank
        // =========================

        rankColumns.forEach(rankName => {

            columns.push({

                headerName: rankName,

                flex: 1,

                minWidth: 120,

                headerClass: 'ag-header-cell-center',

                valueGetter: params => {

                    return params.data?.ranks?.[rankName] ?? null;

                },

                valueFormatter: fmtNum,

                cellStyle: {
                    textAlign: 'center',
                    color: '#096DD9',
                    cursor: 'pointer',
                } as CellStyle,

            });

        });

        // =========================
        // Tổng cộng
        // =========================

        columns.push({

            headerName: 'Tổng',

            field: 'tong',

            flex: 1,

            minWidth: 120,

            headerClass: 'ag-header-cell-center',

            valueFormatter: fmtNum,

            cellStyle: {
                textAlign: 'center',
                color: '#096DD9',
                cursor: 'pointer',
            } as CellStyle,

        });

        return columns;

    }, [
        expanded,
        rankColumns,
    ]);


    const handleCellClicked = (e: CellClickedEvent<FlatRow>) => {
        const field = e.colDef.headerName; // tên hạng
        const unitTitle = e.data?.name; // tên đơn vị

        if (!field) return;

        const unitId = e.data?.id;

        // =========================
        // Click cột Tổng
        // =========================
        if (field === 'Tổng') {
            // console.log("unitId:", unitId);
            // console.log("Không truyền rankId");
            navigate(
                `/analysis-report/member-card-list`,
                {
                    state: {
                        type: 1,
                        listUnit: unitId,
                        listRank: dataFilter?.rankIds,
                        fromMonth: dataFilter?.fromMonth,
                        toMonth: dataFilter?.toMonth,
                        title: `Tổng khách hàng tại đơn vị ${unitTitle}`
                    }
                }
            )
            return;
        }

        const ranks = e.data?.ranks ?? {};

        // =========================
        // Không phải cột Rank
        // =========================
        if (!(field in ranks)) {
            return;
        }

        // =========================
        // Click cột Rank
        // =========================

        const rankTitle = field;

        const rankId = dataRank?.find(
            r => r.title === rankTitle
        )?.id;
        navigate(
            `/analysis-report/member-card-list`,
            {
                state: {
                    type: 1,
                    listUnit: unitId,
                    listRank: rankId,
                    fromMonth: dataFilter?.fromMonth,
                    toMonth: dataFilter?.toMonth,
                    title: `Khách hàng có hạng ${field} tại đơn vị ${unitTitle}`
                }
            }
        )
        // console.log("unitId:", unitId);
        // console.log("rankTitle:", rankTitle);
        // console.log("rankId:", rankId);
        // console.log("value:", ranks[field]);
    };

    return (
        <div className="ag-theme-alpine custom-table-ag-v2" style={{ width: '100%' }}>
            <AgGridReact<FlatRow>
                domLayout="autoHeight"
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={{ resizable: true, sortable: false }}
                suppressMovableColumns
                suppressCellFocus
                rowHeight={44}
                getRowId={(p) => p.data.id}
                getRowStyle={(params) => ({
                    backgroundColor: params.rowIndex % 2 ? '#f7f9fc' : '#ffffff',
                })}
                onCellClicked={handleCellClicked}
            />
        </div>
    );
};

export default ReportTableTotalMembers;
